import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import ProductCard from '../components/ProductCard.jsx'
import { Chip, SectionWrapper } from '../components/primitives/index.js'
import SEO from '../components/SEO.jsx'
import { useProducts } from '../context/ProductsContext.jsx'
import { CATALOG_LINE } from '../config/catalog.js'
import { pages, site, applySeoOverrides } from '../config/seo.js'
import { getDefaultVariant } from '../config/productUtils.js'
import './CatalogPage.css'

/** Tab labels mirror Prod's CatalogPage.jsx CATALOG_TABS exactly — note
 * "Pharmaceuticals" is the real label for the BEAUTY line, not a typo. */
const CATALOG_TABS = [
  { line: CATALOG_LINE.ALL, label: 'All' },
  { line: CATALOG_LINE.VIALS, label: 'Vials' },
  { line: CATALOG_LINE.BEAUTY, label: 'Pharmaceuticals' },
]

const CATALOG_COPY = {
  [CATALOG_LINE.ALL]: { title: 'Shop', body: 'The full LooksLab catalog — research-grade compounds, third-party tested and cold-chain shipped.' },
  [CATALOG_LINE.VIALS]: { title: 'Vials', body: 'Research-grade peptide compounds, batch-verified for purity.' },
  [CATALOG_LINE.BEAUTY]: { title: 'Pharmaceuticals', body: 'Peptide-driven formulations for daily use.' },
}

const SORTS = [
  { id: 'featured', label: 'Featured' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
]

export default function CatalogPage() {
  const { getCatalogProductsForLine, loading, seoSettings } = useProducts()
  const [catalogLine, setCatalogLine] = useState(CATALOG_LINE.ALL)
  const [sort, setSort] = useState('featured')

  const listed = useMemo(() => getCatalogProductsForLine(catalogLine), [catalogLine, getCatalogProductsForLine])

  const sorted = useMemo(() => {
    const list = [...listed]
    if (sort === 'price-asc') list.sort((a, b) => getDefaultVariant(a).price - getDefaultVariant(b).price)
    if (sort === 'price-desc') list.sort((a, b) => getDefaultVariant(b).price - getDefaultVariant(a).price)
    return list
  }, [listed, sort])

  const copy = CATALOG_COPY[catalogLine]

  return (
    <div className="catalog container">
      <SEO
        config={
          catalogLine === CATALOG_LINE.ALL
            ? applySeoOverrides(pages.catalog, seoSettings?.pages?.catalog)
            : { title: copy.title, description: copy.body, canonical: `${site.siteUrl}/catalog` }
        }
      />

      <SectionWrapper className="catalog__header">
        <h1>{copy.title}</h1>
        <p>{copy.body}</p>
      </SectionWrapper>

      <div className="catalog__tabs" role="tablist">
        {CATALOG_TABS.map((t) => (
          <button
            key={t.line}
            role="tab"
            aria-selected={catalogLine === t.line}
            className={`catalog__tab ${catalogLine === t.line ? 'is-active' : ''}`}
            onClick={() => setCatalogLine(t.line)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="catalog__toolbar">
        <div className="catalog__sorts">
          {SORTS.map((s) => (
            <Chip key={s.id} active={sort === s.id} onClick={() => setSort(s.id)}>
              {s.label}
            </Chip>
          ))}
        </div>
        <span className="catalog__count">{sorted.length} products</span>
      </div>

      {loading ? (
        <div className="catalog__loading">Loading catalog&hellip;</div>
      ) : (
        <motion.div
          key={catalogLine}
          className="catalog__grid"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
        >
          {sorted.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </motion.div>
      )}
    </div>
  )
}
